import {PushNotificationsService} from '../services/openapi/services/PushNotificationsService';
import {ApiError} from '../services/openapi/core/ApiError';
import {NotificationPayload} from '../services/openapi/models/NotificationPayload';

export interface NotificationData {
  title: string;
  body: string;
  userId?: string;
  data?: Record<string, any>;
  icon?: string;
  badge?: string;
}

export class PushNotificationHelper {
  /**
   * Sendet eine Push-Benachrichtigung an einen spezifischen Benutzer
   */
  static async sendNotificationToUser(notificationData: NotificationData): Promise<boolean> {
    try {
      console.log('🔔 [DEBUG] Starting sendNotificationToUser with data:', notificationData);
      
      if (!notificationData.userId) {
        console.warn('⚠️ [DEBUG] Keine Benutzer-ID angegeben für Push-Benachrichtigung');
        return false;
      }

      const payload: NotificationPayload = {
        title: notificationData.title,
        body: notificationData.body,
        icon: notificationData.icon,
        badge: notificationData.badge,
        data: notificationData.data || {}
      };

      console.log('🔔 [DEBUG] Payload erstellt:', payload);
      console.log('🔔 [DEBUG] Sende an Benutzer-ID:', notificationData.userId);

      await PushNotificationsService.sendNotificationPushNotificationsSendPost(
        notificationData.userId,
        payload
      );
      
      console.log('✅ [DEBUG] Push-Benachrichtigung erfolgreich gesendet:', notificationData.title);
      return true;
    } catch (error) {
      console.error('❌ [DEBUG] Fehler beim Senden der Push-Benachrichtigung:', error);
      
      if (error instanceof ApiError) {
        console.error('❌ [DEBUG] API Error Details:', {
          status: error.status,
          statusText: error.statusText,
          body: error.body,
          url: error.url,
          request: error.request
        });
      }
      
      return false;
    }
  }

  /**
   * Sendet eine Aufgaben-bezogene Benachrichtigung
   */
  static async sendTaskNotification(
    type: 'created' | 'assigned' | 'completed' | 'updated' | 'overdue',
    taskTitle: string,
    assignedUserId?: string,
    creatorUserId?: string,
    taskId?: string
  ): Promise<void> {
    console.log('🔔 [DEBUG] sendTaskNotification aufgerufen mit:', {
      type,
      taskTitle,
      assignedUserId,
      creatorUserId,
      taskId
    });

    const notifications: NotificationData[] = [];

    switch (type) {
      case 'created':
        if (assignedUserId && assignedUserId !== creatorUserId) {
          console.log('🔔 [DEBUG] Erstelle "created" Benachrichtigung für:', assignedUserId);
          notifications.push({
            title: '📋 Neue Aufgabe zugewiesen',
            body: `Ihnen wurde die Aufgabe "${taskTitle}" zugewiesen.`,
            userId: assignedUserId,
            data: { type: 'task_assigned', taskId }
          });
        } else {
          console.log('🔔 [DEBUG] Keine "created" Benachrichtigung nötig - gleiche Benutzer oder keine Zuweisung');
        }
        break;

      case 'assigned':
        if (assignedUserId) {
          console.log('🔔 [DEBUG] Erstelle "assigned" Benachrichtigung für:', assignedUserId);
          notifications.push({
            title: '📋 Aufgabe zugewiesen',
            body: `Ihnen wurde die Aufgabe "${taskTitle}" zugewiesen.`,
            userId: assignedUserId,
            data: { type: 'task_assigned', taskId }
          });
        } else {
          console.log('🔔 [DEBUG] Keine "assigned" Benachrichtigung nötig - keine Benutzer-ID');
        }
        break;

      case 'completed':
        if (creatorUserId && creatorUserId !== assignedUserId) {
          console.log('🔔 [DEBUG] Erstelle "completed" Benachrichtigung für:', creatorUserId);
          notifications.push({
            title: '✅ Aufgabe abgeschlossen',
            body: `Die Aufgabe "${taskTitle}" wurde abgeschlossen.`,
            userId: creatorUserId,
            data: { type: 'task_completed', taskId }
          });
        } else {
          console.log('🔔 [DEBUG] Keine "completed" Benachrichtigung nötig - gleiche Benutzer oder keine Creator-ID');
        }
        break;

      case 'updated':
        if (assignedUserId && assignedUserId !== creatorUserId) {
          console.log('🔔 [DEBUG] Erstelle "updated" Benachrichtigung für:', assignedUserId);
          notifications.push({
            title: '📝 Aufgabe aktualisiert',
            body: `Die Aufgabe "${taskTitle}" wurde aktualisiert.`,
            userId: assignedUserId,
            data: { type: 'task_updated', taskId }
          });
        } else {
          console.log('🔔 [DEBUG] Keine "updated" Benachrichtigung nötig - gleiche Benutzer oder keine Zuweisung');
        }
        break;

      case 'overdue':
        if (assignedUserId) {
          console.log('🔔 [DEBUG] Erstelle "overdue" Benachrichtigung für:', assignedUserId);
          notifications.push({
            title: '⏰ Aufgabe überfällig',
            body: `Die Aufgabe "${taskTitle}" ist überfällig!`,
            userId: assignedUserId,
            data: { type: 'task_overdue', taskId }
          });
        } else {
          console.log('🔔 [DEBUG] Keine "overdue" Benachrichtigung nötig - keine Benutzer-ID');
        }
        break;
    }

    console.log('🔔 [DEBUG] Zu sendende Benachrichtigungen:', notifications);

    // Alle Benachrichtigungen parallel senden
    const results = await Promise.allSettled(
      notifications.map(notification => this.sendNotificationToUser(notification))
    );

    console.log('🔔 [DEBUG] Ergebnisse der Benachrichtigungen:', results);
  }

  /**
   * Sendet allgemeine System-Benachrichtigungen an mehrere Benutzer
   */
  static async sendSystemNotification(
    title: string,
    body: string,
    userIds: string[],
    data?: Record<string, any>
  ): Promise<void> {
    const notifications = userIds.map(userId => ({
      title,
      body,
      userId,
      data: data || {}
    }));

    await Promise.allSettled(
      notifications.map(notification => this.sendNotificationToUser(notification))
    );
  }

  /**
   * Erstellt eine Test-Benachrichtigung für Debugging
   */
  static async sendTestNotification(userId: string): Promise<boolean> {
    return this.sendNotificationToUser({
      title: '🧪 Test-Benachrichtigung',
      body: 'Dies ist eine Test-Nachricht vom Boehringer System.',
      userId,
      data: { type: 'test', timestamp: new Date().toISOString() }
    });
  }

  /**
   * Plant eine verzögerte Push-Benachrichtigung (für Erinnerungen etc.)
   * WICHTIG: Diese Funktion plant nur clientseitig - für echte geplante Benachrichtigungen
   * sollte eine serverseitige Lösung implementiert werden!
   */
  static scheduleDelayedNotification(
    notificationData: NotificationData,
    delayInMinutes: number
  ): number {
    const timeoutId = window.setTimeout(async () => {
      console.log('⏰ Sende geplante Push-Benachrichtigung...');
      await this.sendNotificationToUser(notificationData);
    }, delayInMinutes * 60 * 1000);

    console.log(`⏰ Benachrichtigung geplant für ${delayInMinutes} Minuten (ID: ${timeoutId})`);
    return timeoutId;
  }

  /**
   * Storniert eine geplante Benachrichtigung
   */
  static cancelScheduledNotification(timeoutId: number): void {
    window.clearTimeout(timeoutId);
    console.log(`⏰ Geplante Benachrichtigung storniert (ID: ${timeoutId})`);
  }

  /**
   * Prüft Aufgaben auf überfällige Deadlines und sendet Benachrichtigungen
   */
  static async checkAndNotifyOverdueTasks(tasks: Array<{
    id: string;
    title: string;
    assigned_to?: string;
    due_date?: string;
    status: string;
  }>): Promise<void> {
    const now = new Date();
    
    for (const task of tasks) {
      if (!task.due_date || !task.assigned_to || task.status === 'COMPLETED') {
        continue;
      }

      const dueDate = new Date(task.due_date);
      if (dueDate < now) {
        console.log(`⏰ Aufgabe "${task.title}" ist überfällig, sende Benachrichtigung...`);
        await this.sendTaskNotification(
          'overdue',
          task.title,
          task.assigned_to,
          undefined,
          task.id
        );
      }
    }
  }
}
